import React, { useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  styled,
  AppBar,
  Box,
  CircularProgress,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Button,
  Divider,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  DirectionsCar as VehicleIcon,
  Business as BusinessIcon,
  ExitToApp as LogoutIcon,
  Speed as SpeedIcon,
  LocalGasStation as FuelIcon,
  Build as BuildIcon,
  BuildCircle as QuickBuildIcon,
  Event as EventIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { LanguageSelector } from '../LanguageSelector';
import { UserInfo } from './UserInfo';
import { NotificationButton } from './NotificationButton';

const drawerWidth = 300;

const Layout: React.FC = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const { t } = useTranslation();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const mainMenuItems = [
    { text: t('dashboard:title'), icon: <DashboardIcon />, path: '/dashboard' },
    { text: t('vehicle.list'), icon: <VehicleIcon />, path: '/vehicles' },
    { text: t('maintenance.title'), icon: <BuildIcon />, path: '/maintenance' },
    { text: t('suppliers.title'), icon: <BusinessIcon />, path: '/suppliers' },
    { text: t('reservation:management.title'), icon: <EventIcon />, path: '/reservations' },
  ];

  const quickEntryItems = [
    { text: t('maintenance.quickEntry.menu'), icon: <QuickBuildIcon />, path: '/quick-maintenance' },
    { text: t('vehicle.mileage.quickEntry'), icon: <SpeedIcon />, path: '/quick-mileage' },
    { text: t('fuelReceipt.quickEntry.menu'), icon: <FuelIcon />, path: '/quick-fuel' },
  ];

  // Éléments du menu pour l'importation de données
  const importItems = [
    { text: 'Importation CSV Carburant', icon: <CloudUploadIcon />, path: '/fuel/import' },
  ];

  const settingsItems = [
    { text: t('organization.dashboard'), icon: <DashboardIcon />, path: '/' },
  ];

  // Add admin path prefix to all routes
  const addPathPrefix = (items: typeof mainMenuItems) =>
    items.map(item => ({
      ...item,
      path: `/admin${item.path.startsWith('/') ? item.path : `/${item.path}`}`
    }));

  const allMenuSections = [
    addPathPrefix(mainMenuItems),
    addPathPrefix(quickEntryItems),
    addPathPrefix(importItems),
    addPathPrefix(settingsItems),
  ];

  const LogoImg = styled('img')(({ theme }) => ({
    height: '40px',
    [theme.breakpoints.down('sm')]: {
      height: '32px',
    },
  }));

  const drawer = (
    <div>
      <Toolbar sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <LogoImg
          src="/logo-verifleet1.png"
          alt="VeriFleet Logo"
        />
      </Toolbar>
      <Divider />
      {allMenuSections.map((section, index) => (
        <React.Fragment key={index}>
          <List>
            {section.map((item) => (
              <ListItemButton
                key={item.text}
                onClick={() => {
                  navigate(item.path);
                  setMobileOpen(false);
                }}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            ))}
          </List>
          <Divider />
        </React.Fragment>
      ))}
      <List>
        <ListItemButton sx={{ justifyContent: 'center', py: 0 }}>
          <LanguageSelector />
        </ListItemButton>
        <ListItemButton onClick={handleSignOut}>
          <ListItemIcon>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText primary={t('auth.logout')} />
        </ListItemButton>
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            {!user?.organizationId && (
              <Typography variant="h6" noWrap component="div">
                {t('common.welcome', 'Welcome')}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <UserInfo />
              <NotificationButton />
            </Box>
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
              <Button color="inherit" onClick={handleSignOut}>
                {t('auth.logout')}
              </Button>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 0,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          mt: '64px', // Height of AppBar
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <React.Suspense
          fallback={
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="200px"
            >
              <CircularProgress />
            </Box>
          }
        >
          <Outlet />
        </React.Suspense>
      </Box>
    </Box>
  );
};

export default Layout;
